import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FlaskConical, Plus, Trash2 } from 'lucide-react';
import { useId, useMemo, useState, type ReactNode, type SelectHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Switch,
  Textarea,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { cn, formatDateTime } from '@/lib/utils';
import { describeWorkflow, eventKey } from './workflow-sentence';
import {
  ACTION_NEEDS,
  available,
  CONDITION_NEEDS,
  CUSTOMER_TEMPLATES,
  LIMITS,
  lookupsApi,
  PLACEHOLDERS,
  TRIGGER_EVENTS,
  triggerSubjects,
  WORKFLOWS_KEY,
  workflowsApi,
  type Action,
  type Condition,
  type Names,
  type TestResult,
  type Trigger,
  type WorkflowDefinition,
} from './workflows-api';

/* ---------------------------------------------------------------------------
   הבונה: "כאשר [טריגר] ובתנאי ש[תנאים] אז [פעולות]", עם המשפט במילים בזמן
   אמת וריצת ניסיון מול אירוע אמיתי (בלי לכתוב כלום). השרת מאמת הכל שוב.
   --------------------------------------------------------------------------- */

const CONDITION_TYPES: Condition['type'][] = [
  'job_type',
  'amount',
  'customer_kind',
  'task_priority',
  'assignee',
  'task_source',
  'task_field',
  'contract_category',
];
const ACTION_TYPES: Action['type'][] = [
  'create_task',
  'add_checklist_items',
  'notify_team',
  'send_customer_message',
  'reminder',
  'set_task_status',
  'purchase_order_draft',
];

function newCondition(type: Condition['type']): Condition {
  switch (type) {
    case 'job_type':
      return { type, op: 'in', jobTypeIds: [] };
    case 'amount':
      return { type, op: 'gte', value: 10_000 };
    case 'customer_kind':
      return { type, value: 'new' };
    case 'task_priority':
      return { type, priorities: [1] };
    case 'assignee':
      return { type, op: 'in', userIds: [] };
    case 'task_source':
      return { type, sources: ['EMAIL'] };
    case 'task_field':
      return { type, key: '', value: '' };
    case 'contract_category':
      return { type, categories: [] };
  }
}

function newAction(type: Action['type']): Action {
  switch (type) {
    case 'create_task':
      return { type, title: '', assign: { mode: 'none' } };
    case 'add_checklist_items':
      return { type, items: [] };
    case 'notify_team':
      return { type, message: '', includeOwners: true, userIds: [], email: false };
    case 'send_customer_message':
      return { type, template: 'satisfaction' };
    case 'reminder':
      return { type, inDays: 7, message: '' };
    case 'set_task_status':
      return { type, status: 'IN_PROGRESS' };
    case 'purchase_order_draft':
      return { type, supplierId: '' };
  }
}

export const EMPTY_DEFINITION: WorkflowDefinition = {
  name: '',
  trigger: { type: 'event', event: 'quote.approved', delayDays: 0 },
  conditions: [],
  actions: [{ type: 'create_task', title: '', assign: { mode: 'none' } }],
};

function NativeSelect({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={cn(
        'h-10 w-full rounded-(--radius-md) border border-border-strong/80 bg-surface px-2 text-sm text-fg hover:border-accent-border',
        className,
      )}
    />
  );
}

function Labeled({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('block space-y-1 text-xs font-medium text-fg-muted', className)}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function MultiCheck({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ id: string; name: string }>;
  value: string[];
  onChange: (v: string[]) => void;
  label: string;
}) {
  return (
    <fieldset className="space-y-1">
      <legend className="text-xs font-medium text-fg-muted">{label}</legend>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {options.map((o) => (
          <label key={o.id} className="flex items-center gap-1.5 text-sm text-fg">
            <Checkbox
              checked={value.includes(o.id)}
              onCheckedChange={(on) =>
                onChange(on ? [...value, o.id] : value.filter((x) => x !== o.id))
              }
            />
            {o.name}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

const int = (v: string, min: number, max: number) =>
  Math.min(max, Math.max(min, Math.trunc(Number(v) || 0)));

export function WorkflowBuilder({
  initial,
  workflowId,
  onClose,
}: {
  initial: WorkflowDefinition;
  workflowId?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation(['workflows', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const base = useId();
  const [def, setDef] = useState<WorkflowDefinition>(initial);
  const [enabled, setEnabled] = useState(true);
  const [test, setTest] = useState<TestResult | null>(null);

  const jobTypes = useQuery({
    queryKey: ['job-type-templates'],
    queryFn: ({ signal }) => lookupsApi.jobTypes(signal),
  });
  const team = useQuery({ queryKey: ['team', 'lookup'], queryFn: ({ signal }) => lookupsApi.team(signal) });
  const needsSuppliers = def.actions.some((a) => a.type === 'purchase_order_draft');
  const suppliers = useQuery({
    queryKey: ['suppliers'],
    queryFn: ({ signal }) => lookupsApi.suppliers(signal),
    enabled: needsSuppliers,
    retry: false,
  });

  const members = useMemo(
    () =>
      (team.data ?? [])
        .filter((m) => m.isActive !== false && m.role !== 'VIEWER')
        .map((m) => ({ id: m.id, name: m.name })),
    [team.data],
  );
  const names: Names = useMemo(
    () => ({
      jobTypes: Object.fromEntries((jobTypes.data ?? []).map((j) => [j.id, j.name])),
      users: Object.fromEntries((team.data ?? []).map((u) => [u.id, u.name])),
      suppliers: Object.fromEntries((suppliers.data ?? []).map((s) => [s.id, s.name])),
    }),
    [jobTypes.data, team.data, suppliers.data],
  );

  const subjects = triggerSubjects(def.trigger);
  const placeholders = Object.entries(PLACEHOLDERS)
    .filter(([, needs]) => available(needs, subjects))
    .map(([k]) => `{{${k}}}`);

  const setTrigger = (trigger: Trigger) => {
    const s = triggerSubjects(trigger);
    // מה שלא קיים בטריגר החדש יורד, כדי שהשמירה לא תיכשל על משהו שלא נראה.
    setDef({
      ...def,
      trigger,
      conditions: def.conditions.filter((c) => available(CONDITION_NEEDS[c.type], s)),
      actions: def.actions.filter((a) => available(ACTION_NEEDS[a.type], s)),
    });
    setTest(null);
  };
  const setCondition = (i: number, c: Condition) =>
    setDef({ ...def, conditions: def.conditions.map((x, j) => (j === i ? c : x)) });
  const setAction = (i: number, a: Action) =>
    setDef({ ...def, actions: def.actions.map((x, j) => (j === i ? a : x)) });

  const save = useMutation({
    mutationFn: () =>
      workflowId ? workflowsApi.update(workflowId, def) : workflowsApi.create({ ...def, enabled }),
    onSuccess: () => {
      toast.success(t('builder.saved'));
      void qc.invalidateQueries({ queryKey: WORKFLOWS_KEY });
      onClose();
    },
    onError: (err: unknown) =>
      toast.error(t('builder.saveFailed'), err instanceof ApiError ? err.message : undefined),
  });
  const runTest = useMutation({
    mutationFn: () => workflowsApi.test({ definition: def }),
    onSuccess: setTest,
    onError: (err: unknown) =>
      toast.error(t('builder.saveFailed'), err instanceof ApiError ? err.message : undefined),
  });

  const sentence = describeWorkflow(t, def, names);
  const busy = save.isPending || runTest.isPending;

  return (
    <Dialog open onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {workflowId ? t('builder.editTitle') : t('builder.createTitle')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="max-h-[70vh] space-y-5 overflow-y-auto">
          <Labeled label={t('builder.name')}>
            <Input
              id={`${base}-name`}
              value={def.name}
              maxLength={120}
              onChange={(e) => setDef({ ...def, name: e.target.value })}
            />
          </Labeled>

          {/* כאשר */}
          <section className="space-y-2 rounded-(--radius-md) border border-border p-3">
            <h3 className="text-sm font-semibold text-fg">{t('builder.when')}</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              <NativeSelect
                aria-label={t('builder.when')}
                value={def.trigger.type}
                onChange={(e) => {
                  const type = e.target.value as Trigger['type'];
                  setTrigger(
                    type === 'event'
                      ? { type, event: 'quote.approved', delayDays: 0 }
                      : type === 'daily'
                        ? { type, hour: 8 }
                        : { type, daysBefore: 14 },
                  );
                }}
              >
                {(['event', 'daily', 'contract_notice'] as const).map((k) => (
                  <option key={k} value={k}>
                    {t(`trigger.type.${k}`)}
                  </option>
                ))}
              </NativeSelect>
              {def.trigger.type === 'event' ? (
                <NativeSelect
                  aria-label={t('trigger.type.event')}
                  value={def.trigger.event}
                  onChange={(e) =>
                    setTrigger({
                      type: 'event',
                      event: e.target.value as (typeof TRIGGER_EVENTS)[number],
                      delayDays: def.trigger.type === 'event' ? def.trigger.delayDays : 0,
                    })
                  }
                >
                  {TRIGGER_EVENTS.map((ev) => (
                    <option key={ev} value={ev}>
                      {t(eventKey(ev))}
                    </option>
                  ))}
                </NativeSelect>
              ) : null}
              {def.trigger.type === 'event' ? (
                <Labeled label={t('builder.delayDays')}>
                  <Input
                    type="number"
                    min={0}
                    max={60}
                    value={def.trigger.delayDays ?? 0}
                    onChange={(e) =>
                      setTrigger({
                        ...(def.trigger as Extract<Trigger, { type: 'event' }>),
                        delayDays: int(e.target.value, 0, 60),
                      })
                    }
                  />
                </Labeled>
              ) : def.trigger.type === 'daily' ? (
                <Labeled label={t('builder.hour')}>
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={def.trigger.hour}
                    onChange={(e) =>
                      setTrigger({ type: 'daily', hour: int(e.target.value, 0, 23) })
                    }
                  />
                </Labeled>
              ) : (
                <Labeled label={t('builder.daysBefore')}>
                  <Input
                    type="number"
                    min={0}
                    max={90}
                    value={def.trigger.daysBefore}
                    onChange={(e) =>
                      setTrigger({
                        type: 'contract_notice',
                        daysBefore: int(e.target.value, 0, 90),
                      })
                    }
                  />
                </Labeled>
              )}
            </div>
          </section>

          {/* ובתנאי ש */}
          <section className="space-y-2 rounded-(--radius-md) border border-border p-3">
            <h3 className="text-sm font-semibold text-fg">{t('builder.and')}</h3>
            {def.conditions.map((c, i) => (
              <div key={i} className="space-y-2 rounded-(--radius-sm) bg-surface-sunken p-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{t(`condition.type.${c.type}`)}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setDef({ ...def, conditions: def.conditions.filter((_, j) => j !== i) })
                    }
                  >
                    <Trash2 aria-hidden />
                    {t('builder.remove')}
                  </Button>
                </div>
                <ConditionEditor
                  condition={c}
                  onChange={(x) => setCondition(i, x)}
                  jobTypes={jobTypes.data ?? []}
                  members={members}
                />
              </div>
            ))}
            {def.conditions.length < LIMITS.maxConditions ? (
              <NativeSelect
                aria-label={t('builder.addCondition')}
                value=""
                onChange={(e) =>
                  e.target.value &&
                  setDef({
                    ...def,
                    conditions: [
                      ...def.conditions,
                      newCondition(e.target.value as Condition['type']),
                    ],
                  })
                }
              >
                <option value="">+ {t('builder.addCondition')}</option>
                {CONDITION_TYPES.filter((k) => available(CONDITION_NEEDS[k], subjects)).map((k) => (
                  <option key={k} value={k}>
                    {t(`condition.type.${k}`)}
                  </option>
                ))}
              </NativeSelect>
            ) : null}
          </section>

          {/* אז */}
          <section className="space-y-2 rounded-(--radius-md) border border-border p-3">
            <h3 className="text-sm font-semibold text-fg">{t('builder.then')}</h3>
            {def.actions.map((a, i) => (
              <div key={i} className="space-y-2 rounded-(--radius-sm) bg-surface-sunken p-2">
                <div className="flex items-center justify-between gap-2">
                  <NativeSelect
                    aria-label={t('builder.then')}
                    className="max-w-56"
                    value={a.type}
                    onChange={(e) => setAction(i, newAction(e.target.value as Action['type']))}
                  >
                    {ACTION_TYPES.filter((k) => available(ACTION_NEEDS[k], subjects)).map((k) => (
                      <option key={k} value={k}>
                        {t(`action.type.${k}`)}
                      </option>
                    ))}
                  </NativeSelect>
                  {def.actions.length > 1 ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setDef({ ...def, actions: def.actions.filter((_, j) => j !== i) })
                      }
                    >
                      <Trash2 aria-hidden />
                      {t('builder.remove')}
                    </Button>
                  ) : null}
                </div>
                <ActionEditor
                  action={a}
                  onChange={(x) => setAction(i, x)}
                  jobTypes={jobTypes.data ?? []}
                  members={members}
                  suppliers={suppliers.data ?? []}
                  hasJobContext={subjects.includes('task') || subjects.includes('quote')}
                />
              </div>
            ))}
            {def.actions.length < LIMITS.maxActions ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  setDef({ ...def, actions: [...def.actions, newAction('notify_team')] })
                }
              >
                <Plus aria-hidden />
                {t('builder.addAction')}
              </Button>
            ) : null}
            <p className="text-2xs text-fg-subtle [unicode-bidi:plaintext]">
              {t('builder.placeholdersHint', { list: placeholders.join(' ') })}
            </p>
          </section>

          <section className="rounded-(--radius-md) bg-accent-subtle p-3">
            <h3 className="text-xs font-semibold text-fg-muted">{t('builder.preview')}</h3>
            <p data-testid="workflow-sentence" className="mt-1 text-sm text-fg">
              {sentence}
            </p>
          </section>

          {test ? <TestPanel result={test} /> : null}

          {!workflowId ? (
            <label className="flex items-center gap-2 text-sm text-fg">
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                aria-label={t('builder.enabledOnSave')}
              />
              {t('builder.enabledOnSave')}
            </label>
          ) : null}
        </DialogBody>
        <DialogFooter className="flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => runTest.mutate()}
            loading={runTest.isPending}
            disabled={busy}
          >
            <FlaskConical aria-hidden />
            {t('builder.test')}
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={busy || !def.name.trim()}
          >
            {t('builder.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

type Opt = { id: string; name: string };

function ConditionEditor({
  condition: c,
  onChange,
  jobTypes,
  members,
}: {
  condition: Condition;
  onChange: (c: Condition) => void;
  jobTypes: Opt[];
  members: Opt[];
}) {
  const { t } = useTranslation('workflows');
  switch (c.type) {
    case 'job_type':
      return (
        <div className="space-y-2">
          <NativeSelect
            aria-label={t('condition.type.job_type')}
            value={c.op}
            onChange={(e) => onChange({ ...c, op: e.target.value as 'in' | 'not_in' })}
          >
            <option value="in">{t('condition.op.in')}</option>
            <option value="not_in">{t('condition.op.not_in')}</option>
          </NativeSelect>
          <MultiCheck
            label={t('builder.chooseMany')}
            options={jobTypes}
            value={c.jobTypeIds}
            onChange={(v) => onChange({ ...c, jobTypeIds: v })}
          />
        </div>
      );
    case 'amount':
      return (
        <div className="grid grid-cols-2 gap-2">
          <NativeSelect
            aria-label={t('condition.type.amount')}
            value={c.op}
            onChange={(e) => onChange({ ...c, op: e.target.value as 'gte' | 'lte' })}
          >
            <option value="gte">{t('condition.op.gte')}</option>
            <option value="lte">{t('condition.op.lte')}</option>
          </NativeSelect>
          <Input
            type="number"
            min={0}
            aria-label={t('builder.amount')}
            value={c.value}
            onChange={(e) => onChange({ ...c, value: Math.max(0, Number(e.target.value) || 0) })}
          />
        </div>
      );
    case 'customer_kind':
      return (
        <NativeSelect
          aria-label={t('condition.type.customer_kind')}
          value={c.value}
          onChange={(e) => onChange({ ...c, value: e.target.value as 'new' | 'returning' })}
        >
          <option value="new">{t('condition.customer.new')}</option>
          <option value="returning">{t('condition.customer.returning')}</option>
        </NativeSelect>
      );
    case 'task_priority':
      return (
        <MultiCheck
          label={t('builder.chooseMany')}
          options={(['1', '2', '3'] as const).map((p) => ({
            id: p,
            name: t(`condition.priority.${p}`),
          }))}
          value={c.priorities.map(String)}
          onChange={(v) => onChange({ ...c, priorities: v.map(Number) })}
        />
      );
    case 'assignee':
      return (
        <div className="space-y-2">
          <NativeSelect
            aria-label={t('condition.type.assignee')}
            value={c.op}
            onChange={(e) => onChange({ ...c, op: e.target.value as 'in' | 'none' })}
          >
            <option value="in">{t('condition.op.in')}</option>
            <option value="none">{t('condition.op.none')}</option>
          </NativeSelect>
          {c.op === 'in' ? (
            <MultiCheck
              label={t('builder.chooseMany')}
              options={members}
              value={c.userIds}
              onChange={(v) => onChange({ ...c, userIds: v })}
            />
          ) : null}
        </div>
      );
    case 'task_source':
      return (
        <MultiCheck
          label={t('builder.chooseMany')}
          options={(['EMAIL', 'MANUAL', 'CUSTOMER_LINK', 'QUOTE'] as const).map((s) => ({
            id: s,
            name: t(`condition.source.${s}`),
          }))}
          value={c.sources}
          onChange={(v) => onChange({ ...c, sources: v as typeof c.sources })}
        />
      );
    case 'task_field':
      return (
        <div className="grid grid-cols-2 gap-2">
          <Input
            aria-label={t('builder.fieldKey')}
            placeholder={t('builder.fieldKey')}
            value={c.key}
            onChange={(e) => onChange({ ...c, key: e.target.value })}
          />
          <Input
            aria-label={t('builder.fieldValue')}
            placeholder={t('builder.fieldValue')}
            value={c.value}
            onChange={(e) => onChange({ ...c, value: e.target.value })}
          />
        </div>
      );
    case 'contract_category':
      return (
        <Input
          aria-label={t('builder.categories')}
          placeholder={t('builder.categories')}
          value={c.categories.join(', ')}
          onChange={(e) =>
            onChange({
              ...c,
              categories: e.target.value
                .split(',')
                .map((x) => x.trim())
                .filter(Boolean),
            })
          }
        />
      );
  }
}

function ActionEditor({
  action: a,
  onChange,
  jobTypes,
  members,
  suppliers,
  hasJobContext,
}: {
  action: Action;
  onChange: (a: Action) => void;
  jobTypes: Opt[];
  members: Opt[];
  suppliers: Opt[];
  hasJobContext: boolean;
}) {
  const { t } = useTranslation('workflows');
  switch (a.type) {
    case 'create_task': {
      const assign = a.assign ?? { mode: 'none' as const };
      const assignValue = assign.mode === 'user' ? assign.userId : assign.mode;
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          <Labeled label={t('builder.taskTitle')} className="sm:col-span-2">
            <Input
              value={a.title}
              maxLength={200}
              onChange={(e) => onChange({ ...a, title: e.target.value })}
            />
          </Labeled>
          <Labeled label={t('condition.type.job_type')}>
            <NativeSelect
              value={a.jobTypeId ?? ''}
              onChange={(e) => onChange({ ...a, jobTypeId: e.target.value || undefined })}
            >
              <option value="">
                {hasJobContext ? t('builder.jobTypeSame') : t('builder.choose')}
              </option>
              {jobTypes.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name}
                </option>
              ))}
            </NativeSelect>
          </Labeled>
          <Labeled label={t('builder.dueInDays')}>
            <Input
              type="number"
              min={0}
              max={60}
              value={a.dueInDays ?? ''}
              onChange={(e) =>
                onChange({
                  ...a,
                  dueInDays: e.target.value === '' ? undefined : int(e.target.value, 0, 60),
                })
              }
            />
          </Labeled>
          <Labeled label={t('builder.assign')} className="sm:col-span-2">
            <NativeSelect
              value={assignValue}
              onChange={(e) => {
                const v = e.target.value;
                onChange({
                  ...a,
                  assign: v === 'none' || v === 'auto' ? { mode: v } : { mode: 'user', userId: v },
                });
              }}
            >
              <option value="none">{t('builder.assignNone')}</option>
              <option value="auto">{t('builder.assignAuto')}</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </NativeSelect>
          </Labeled>
        </div>
      );
    }
    case 'add_checklist_items':
      return (
        <Labeled label={t('builder.items')}>
          <Textarea
            value={a.items.join('\n')}
            onChange={(e) =>
              onChange({
                ...a,
                items: e.target.value
                  .split('\n')
                  .map((x) => x.trim())
                  .filter(Boolean)
                  .slice(0, 20),
              })
            }
          />
        </Labeled>
      );
    case 'notify_team':
      return (
        <div className="space-y-2">
          <Labeled label={t('builder.message')}>
            <Textarea
              value={a.message}
              maxLength={500}
              onChange={(e) => onChange({ ...a, message: e.target.value })}
            />
          </Labeled>
          <label className="flex items-center gap-1.5 text-sm text-fg">
            <Checkbox
              checked={a.includeOwners ?? true}
              onCheckedChange={(on) => onChange({ ...a, includeOwners: on === true })}
            />
            {t('builder.includeOwners')}
          </label>
          <MultiCheck
            label={t('builder.chooseMany')}
            options={members}
            value={a.userIds ?? []}
            onChange={(v) => onChange({ ...a, userIds: v })}
          />
          <label className="flex items-center gap-1.5 text-sm text-fg">
            <Checkbox
              checked={a.email ?? false}
              onCheckedChange={(on) => onChange({ ...a, email: on === true })}
            />
            {t('builder.alsoEmail')}
          </label>
        </div>
      );
    case 'send_customer_message':
      return (
        <Labeled label={t('builder.template')}>
          <NativeSelect
            value={a.template}
            onChange={(e) =>
              onChange({ ...a, template: e.target.value as (typeof CUSTOMER_TEMPLATES)[number] })
            }
          >
            {CUSTOMER_TEMPLATES.map((k) => (
              <option key={k} value={k}>
                {t(`action.template.${k}`)}
              </option>
            ))}
          </NativeSelect>
        </Labeled>
      );
    case 'reminder':
      return (
        <div className="grid gap-2 sm:grid-cols-[8rem_1fr]">
          <Labeled label={t('builder.inDays')}>
            <Input
              type="number"
              min={1}
              max={90}
              value={a.inDays}
              onChange={(e) => onChange({ ...a, inDays: int(e.target.value, 1, 90) })}
            />
          </Labeled>
          <Labeled label={t('builder.message')}>
            <Input
              value={a.message}
              maxLength={500}
              onChange={(e) => onChange({ ...a, message: e.target.value })}
            />
          </Labeled>
        </div>
      );
    case 'set_task_status':
      return null;
    case 'purchase_order_draft':
      return (
        <Labeled label={t('builder.supplier')}>
          <NativeSelect
            value={a.supplierId}
            onChange={(e) => onChange({ ...a, supplierId: e.target.value })}
          >
            <option value="">{t('builder.choose')}</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </NativeSelect>
        </Labeled>
      );
  }
}

export function TestPanel({ result }: { result: TestResult }) {
  const { t } = useTranslation('workflows');
  if (!result.event || !result.evaluation) {
    return (
      <p className="rounded-(--radius-md) border border-border p-3 text-sm text-fg-muted">
        {t('test.noEvent')}
      </p>
    );
  }
  const ev = result.evaluation;
  const runs = ev.status !== 'SKIPPED';
  return (
    <section
      className="space-y-2 rounded-(--radius-md) border border-border p-3 text-sm"
      aria-live="polite"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-fg">{t('test.title')}</h3>
        <Badge tone={runs ? 'success' : 'warning'}>
          {runs ? t('test.wouldRun') : t('test.wouldSkip')}
        </Badge>
      </div>
      <p className="text-xs text-fg-muted">
        {t('test.event', { name: result.event.name, at: formatDateTime(result.event.at) })}
      </p>
      {ev.reason && ev.reason !== 'conditions' ? (
        <p className="text-xs text-warning">{ev.detail}</p>
      ) : null}
      <ul className="space-y-0.5 text-xs">
        {ev.conditions.map((c) => (
          <li key={c.index} className={c.passed ? 'text-success' : 'text-danger'}>
            {c.passed
              ? t('test.conditionPassed', { n: c.index + 1 })
              : t('test.conditionFailed', { n: c.index + 1, detail: c.detail ?? '' })}
          </li>
        ))}
      </ul>
      <ul className="space-y-0.5 text-xs">
        {ev.actions.map((a) => (
          <li key={a.index}>
            <span className="font-medium">{t(`runs.action.${a.status}`)}</span> ·{' '}
            {a.plan
              ? Object.values(a.plan)
                  .filter((v) => typeof v === 'string' || typeof v === 'number')
                  .join(' · ')
              : (a.detail ?? '')}
          </li>
        ))}
      </ul>
      <p className="text-2xs text-fg-subtle">{t('test.nothingWritten')}</p>
    </section>
  );
}
